import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { notification } from 'antd';
import { useMutation, useQuery } from 'react-query';
import activitiesServices from './activities.services';
import notificationError from '@/utils/functions/notificationError';
import { getErrorMessageGP } from '../status/getErrorMessageGP';
import {
  GPActivityCalendarRequest,
  GPActivityListRequest,
} from './activities.types';
import { PENDING_ACTIVITY_LIST_KEY } from '../pending-activities/pending-activities.hooks';

export const ACTIVITY_CREAT_KEY = 'activity/create';
export const ACTIVITY_DELETE_KEY = 'activity/delete';
export const ACTIVITY_DETAIL_KEY = 'activity/detail';
export const ACTIVITY_EDIT_KEY = 'activity/edit';
export const ACTIVITY_LIST_KEY = 'activity/list';
export const ACTIVITY_PENDING_LIST_KEY = 'activity/pending/list';
export const ACTIVITY_OBSERVATION_CREAT_KEY = 'activity/observation/create';
export const ACTIVITY_OBSERVATION_LIST_KEY = 'activity/observation/list';
export const ACTIVITY_OBSERVATION_DELETE_KEY = 'activity/observation/delete';
export const ACTIVITY_OBSERVATION_EDIT_KEY = 'activity/observation/edit';
export const ACTIVITY_STATUS_CHANGE_KEY = 'activity/status-change';
export const ACTIVITY_SITUATION_CHANGE_KEY = 'activity/situation-change';
export const ACTIVITY_CALENDAR_KEY = 'activity/calendar';
export const ACTIVITY_SERVICES_ADD_KEY = 'activity/services/add';
export const ACTIVITY_SERVICES_EDIT_KEY = 'activity/services/edit';
export const ACTIVITY_SERVICES_DETAIL_KEY = 'activity/services/detail';
export const ACTIVITY_SERVICES_DURATION_KEY = 'activity/services/duration';
export const ACTIVITY_FOR_DIARY_KEY = 'activity/for-diary';
export const ACTIVITY_BY_DIARY_KEY = 'activity/by-diary';
export const ACTIVITY_HISTORY_KEY = 'activity/history';
export const ACTIVITY_GENERATE_WORKORDER_KEY = 'activity/generate-work-order';

export function useActivityCreate() {
  const queryChanel = useQueryChanel();
  return useMutation({
    mutationKey: [ACTIVITY_CREAT_KEY],
    mutationFn: activitiesServices.create,
    onSuccess(_, vars) {
      notification.success({ message: 'Atividade cadastrado com sucesso' });

      if (vars.statusId) {
        queryChanel({
          invalidateQueries: [[ACTIVITY_LIST_KEY], [ACTIVITY_CALENDAR_KEY]],
        });
      } else {
        queryChanel({
          invalidateQueries: [
            [PENDING_ACTIVITY_LIST_KEY],
            [ACTIVITY_CALENDAR_KEY],
          ],
        });
      }
    },
    onError(err: Error) {
      notificationError({
        color: 'red',
        message: getErrorMessageGP(err),
      });
    },
  });
}

export function useActivityDelete() {
  const queryChanel = useQueryChanel();
  return useMutation({
    mutationKey: [ACTIVITY_DELETE_KEY],
    mutationFn: activitiesServices.delete,
    onSuccess() {
      notification.success({ message: 'Atividade excluída com sucesso' });

      queryChanel({
        invalidateQueries: [
          [PENDING_ACTIVITY_LIST_KEY],
          [ACTIVITY_LIST_KEY],
          [ACTIVITY_CALENDAR_KEY],
          [ACTIVITY_DETAIL_KEY],
        ],
      });
    },
    onError(err: Error) {
      notificationError({
        color: 'red',
        message: getErrorMessageGP(err),
      });
    },
  });
}

export function useActivityRestore() {
  const queryChanel = useQueryChanel();
  return useMutation({
    mutationKey: [ACTIVITY_DELETE_KEY],
    mutationFn: activitiesServices.restore,
    onSuccess() {
      notification.success({ message: 'Atividade restaurada com sucesso' });

      queryChanel({
        invalidateQueries: [
          [PENDING_ACTIVITY_LIST_KEY],
          [ACTIVITY_LIST_KEY],
          [ACTIVITY_CALENDAR_KEY],
          [ACTIVITY_DETAIL_KEY],
        ],
      });
    },
    onError(err: Error) {
      notificationError({
        color: 'red',
        message: getErrorMessageGP(err),
      });
    },
  });
}

export function useActivityEdit() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationKey: [ACTIVITY_EDIT_KEY],
    mutationFn: activitiesServices.edit,
    onSuccess(_, vars) {
      notification.success({
        message: 'Atividade atualizada com sucesso',
      });

      queryChanel({
        invalidateQueries: [
          [ACTIVITY_DETAIL_KEY, { activityId: vars.activityId }],
          [ACTIVITY_HISTORY_KEY],
        ],
      });

      queryChanel({
        invalidateQueries: [
          [ACTIVITY_LIST_KEY],
          [PENDING_ACTIVITY_LIST_KEY],
          [ACTIVITY_CALENDAR_KEY],
        ],
      });
    },
    onError(err: Error) {
      notificationError({
        message: getErrorMessageGP(err),
      });
    },
  });
}

export function useActivityDetail(activityId?: string, enabled = true) {
  return useQuery({
    queryKey: [ACTIVITY_DETAIL_KEY, { activityId }],
    queryFn: () => activitiesServices.detail(activityId),
    enabled: !!activityId && enabled,
  });
}

export function useActivityList(params?: GPActivityListRequest) {
  return useQuery({
    queryKey: [ACTIVITY_LIST_KEY, { ...params }],
    enabled: !!params?.companyIds?.length,
    queryFn: () =>
      activitiesServices.list({
        paginated: false,
        limit: 10,
        ...params,
      }),
  });
}

export function useActivityListPaginate(p: GPActivityListRequest) {
  return useMutation({
    mutationFn: (params: GPActivityListRequest) =>
      activitiesServices.list({
        paginated: true,
        limit: 10,
        ...p,
        ...params,
      }),
  });
}

export function useActivityStatusChange() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationKey: [ACTIVITY_STATUS_CHANGE_KEY],
    mutationFn: activitiesServices.statusChange,
    onSuccess() {
      notification.success({
        message: 'Status da atividade alterada',
      });

      queryChanel({
        invalidateQueries: [
          [ACTIVITY_LIST_KEY],
          [PENDING_ACTIVITY_LIST_KEY],
          [ACTIVITY_HISTORY_KEY],
        ],
      });
    },
    onError(err: Error) {
      notificationError({
        message: getErrorMessageGP(err),
      });
    },
  });
}

export function useActivityCalendar(params: GPActivityCalendarRequest) {
  return useQuery({
    queryKey: [ACTIVITY_CALENDAR_KEY, { ...params }],
    queryFn: () => activitiesServices.calendar(params),
    enabled: !!params?.month && !!params?.year && !!params.companyIds?.length,
  });
}

export function useActivityGenerateWorkOrder() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationKey: [ACTIVITY_GENERATE_WORKORDER_KEY],
    mutationFn: activitiesServices.generateWorkOrder,
    onSuccess(_, activityId) {
      notification.success({
        message: 'Orçamento gerado com sucesso',
      });

      queryChanel({
        invalidateQueries: [
          [ACTIVITY_DETAIL_KEY, { activityId }],
          [ACTIVITY_HISTORY_KEY],
          [ACTIVITY_LIST_KEY],
          [PENDING_ACTIVITY_LIST_KEY],
        ],
      });
    },
    onError(err: Error) {
      notificationError({
        message: getErrorMessageGP(err),
      });
    },
  });
}

export function useActivityObservationList(activityId?: string) {
  return useQuery({
    queryKey: [ACTIVITY_OBSERVATION_LIST_KEY, activityId],
    queryFn: () => activitiesServices.observationList(activityId),
    enabled: !!activityId,
  });
}

export function useActivityObservationEdit() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: activitiesServices.observationEdit,
    onSuccess() {
      notification.success({
        message: 'Observação editada com sucesso',
      });

      queryChanel({
        invalidateQueries: [[ACTIVITY_OBSERVATION_LIST_KEY]],
      });
    },
    onError(err: Error) {
      notificationError({
        color: 'red',
        message: getErrorMessageGP(err),
      });
    },
  });
}

export function useActivityObservationCreate() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationKey: [ACTIVITY_OBSERVATION_CREAT_KEY],
    mutationFn: activitiesServices.observationCreate,
    onSuccess(_, variables) {
      notification.success({
        message: 'Observação cadastrada com sucesso',
      });

      queryChanel({
        invalidateQueries: [
          [ACTIVITY_OBSERVATION_LIST_KEY, variables.activityId],
          [ACTIVITY_HISTORY_KEY],
        ],
      });
    },
    onError(error: Error) {
      notificationError({
        color: 'red',
        message: getErrorMessageGP(error),
      });
    },
  });
}

export function useActivityObservationDelete() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationKey: [ACTIVITY_OBSERVATION_DELETE_KEY],
    mutationFn: activitiesServices.observationDelete,
    onSuccess(_, variables) {
      notification.success({
        message: 'Observação deletada com sucesso',
      });

      queryChanel({
        invalidateQueries: [
          [ACTIVITY_OBSERVATION_LIST_KEY, variables.activityId],
          [ACTIVITY_HISTORY_KEY],
        ],
      });
    },
    onError(err: Error) {
      notificationError({
        color: 'red',
        message: getErrorMessageGP(err),
      });
    },
  });
}
